import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Switch,
  Tabs,
  Spin,
} from 'antd';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { pageService, Page, PageType } from '../services/pageService';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Pages: React.FC = () => {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPage, setEditingPage] = useState<Page | null>(null);
  const [form] = Form.useForm();
  const quillRef = useRef<Quill | null>(null);
  const quillContainerRef = useRef<HTMLDivElement>(null);
  const [previewContent, setPreviewContent] = useState<string>('');

  useEffect(() => {
    fetchPages();
  }, []);

  // Initialize Quill editor when modal opens
  useEffect(() => {
    if (modalVisible && quillContainerRef.current) {
      // Clean up existing Quill instance
      if (quillRef.current) {
        quillRef.current = null;
      }

      // Create new Quill instance
      quillRef.current = new Quill(quillContainerRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link', 'image', 'video'],
            ['blockquote', 'code-block'],
            ['clean']
          ]
        },
        placeholder: 'Start writing your content here...',
      });

      // Set initial content if editing
      if (editingPage?.content) {
        quillRef.current.root.innerHTML = editingPage.content;
        setPreviewContent(editingPage.content);
      }

      // Sync Quill content with form and preview
      quillRef.current.on('text-change', () => {
        const content = quillRef.current?.root.innerHTML || '';
        form.setFieldsValue({ content });
        setPreviewContent(content);
      });
    }

    return () => {
      // Cleanup on unmount
      if (quillRef.current) {
        quillRef.current = null;
      }
    };
  }, [modalVisible, editingPage, form]);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await pageService.getPages(undefined, 'all'); // include inactive pages
      setPages(response.data || []);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch pages');
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate slug from title
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    const slug = title.toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    form.setFieldsValue({ slug });
  };

  const handleAdd = () => {
    setEditingPage(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, order: 0 });
    setPreviewContent('');
    setModalVisible(true);
  };

  const handleEdit = (page: Page) => {
    setEditingPage(page);
    form.setFieldsValue(page);
    setPreviewContent(page.content || '');
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      // Get content from Quill editor
      const content = quillRef.current?.root.innerHTML || values.content || '';
      
      if (!content || content.trim() === '<p><br></p>') {
        message.error('Please enter page content');
        return;
      }

      const pageData = {
        ...values,
        content,
      };

      if (editingPage) {
        await pageService.updatePage(editingPage._id, pageData);
        message.success('Page updated successfully');
      } else {
        await pageService.createPage(pageData);
        message.success('Page created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      setPreviewContent('');
      if (quillRef.current) {
        quillRef.current.root.innerHTML = '';
      }
      fetchPages();
    } catch (error: any) {
      message.error(error.message || 'Failed to save page');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await pageService.togglePageStatus(id);
      message.success(`Page ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchPages();
    } catch (error: any) {
      message.error(error.message || 'Failed to toggle page status');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await pageService.deletePage(id);
      message.success('Page deleted successfully');
      fetchPages();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete page');
    }
  };

  const pageTypes: PageType[] = ['help', 'support', 'contact', 'about', 'terms', 'privacy', 'custom'];

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: PageType) => (
        <Tag color="blue">{type.charAt(0).toUpperCase() + type.slice(1)}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: boolean, record: Page) => (
        <Space>
          <Switch
            checked={isActive}
            onChange={() => handleToggleStatus(record._id, isActive)}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
          <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>
        </Space>
      ),
    },
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      width: 80,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Page) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this page?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>CMS Pages</Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchPages}>
                Refresh
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Add Page
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={pages}
          rowKey="_id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingPage ? '✏️ Edit Page' : '✨ Create New Page'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setPreviewContent('');
          if (quillRef.current) {
            quillRef.current.root.innerHTML = '';
          }
        }}
        onOk={() => form.submit()}
        width={1000}
        style={{ top: 20 }}
        okText={editingPage ? 'Update Page' : 'Create Page'}
        cancelText="Cancel"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item 
                name="title" 
                label="Page Title" 
                rules={[{ required: true, message: 'Please enter page title' }]}
              >
                <Input 
                  placeholder="Enter page title"
                  onChange={handleTitleChange}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                name="type" 
                label="Page Type" 
                rules={[{ required: true, message: 'Please select page type' }]}
              >
                <Select placeholder="Select type" size="large">
                  {pageTypes.map((type) => (
                    <Option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item 
            name="slug" 
            label="URL Slug" 
            rules={[{ required: true, message: 'Please enter URL slug' }]}
          >
            <Input 
              placeholder="url-slug-for-this-page"
              addonBefore="https://jivbook.com/"
              size="large"
            />
          </Form.Item>

          <Form.Item label="Content" required>
            <Tabs
              defaultActiveKey="editor"
              items={[
                {
                  key: 'editor',
                  label: '✏️ Editor',
                  children: (
                    <div style={{ border: '1px solid #d9d9d9', borderRadius: '4px', backgroundColor: '#fff' }}>
                      <div ref={quillContainerRef} style={{ minHeight: '400px' }} />
                    </div>
                  ),
                },
                {
                  key: 'preview',
                  label: '👁️ Preview',
                  children: (
                    <div 
                      style={{ 
                        border: '1px solid #d9d9d9', 
                        borderRadius: '4px',
                        padding: '20px',
                        minHeight: '400px',
                        backgroundColor: '#fff'
                      }}
                      dangerouslySetInnerHTML={{ __html: previewContent || '<p style="color: #999; text-align: center; padding: 50px;">Start typing to see preview...</p>' }}
                    />
                  ),
                },
              ]}
            />
            <Form.Item name="content" hidden>
              <Input />
            </Form.Item>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="metaTitle" label="SEO Meta Title">
                <Input placeholder="Optional: SEO meta title" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="order" label="Display Order" initialValue={0}>
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="metaDescription" label="SEO Meta Description">
            <TextArea rows={2} placeholder="Optional: SEO meta description" />
          </Form.Item>

          <Form.Item name="isActive" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" /> Page is active
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Pages;

